import { Mail } from 'lucide-preact';
import { route } from 'preact-router';
import { useState } from 'preact/hooks';
import { IdentityNumberField } from '../../components/identity/IdentityNumberField.js';
import { Button } from '../../components/ui/Button.js';
import { SupportLink } from '../../components/ui/SupportLink.js';
import { FIELD_LABEL_CLASS } from '../../components/ui/fieldLabel.js';
import { api } from '../../services/api.js';
import {
  type IdentityNumberValue,
  identityNumberBody,
  identityNumberError,
} from '../../utils/identityNumber.js';
import { PASSWORD_RULE_TEXT, validateRegisterFields } from '../../utils/validation.js';
import { NAME_IC_HINT, NAME_IC_HINT_ID } from './nameHint.js';

interface RegisterPageProps {
  path?: string;
}

/**
 * A field's error message.
 *
 * `role="alert"` so a screen-reader user learns why a blocked submit did nothing — `admin`'s
 * `FormField` has done this since 061, and `ProfileEditPage`'s bare span does not, which is the gap
 * this closes on the app side.
 */
function FieldError({ field, message }: { field: string; message?: string }) {
  if (!message) return null;
  return (
    <span
      id={`${field}-error`}
      data-testid={`${field.replace('_', '-')}-error`}
      role="alert"
      className="text-sm text-error"
    >
      {message}
    </span>
  );
}

export function RegisterPage(_props: RegisterPageProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [done, setDone] = useState(false);
  // 366 (#1223): the MyKad or passport number, required, one account per person.
  const [identity, setIdentity] = useState<IdentityNumberValue>({ kind: 'mykad', number: '' });
  const [identityTaken, setIdentityTaken] = useState(false);

  async function handleSubmit(e: Event) {
    e.preventDefault();
    const fd = new FormData(e.target as HTMLFormElement);

    const values = {
      first_name: String(fd.get('first_name') ?? '').trim(),
      last_name: String(fd.get('last_name') ?? '').trim(),
      email: String(fd.get('email') ?? '')
        .trim()
        .toLowerCase(),
      phone: String(fd.get('phone') ?? ''),
      password: String(fd.get('password') ?? ''),
    };

    // 140: validated here as well as at the boundary, because `ApiError` carries no field information
    // (`services/api.ts`, contract C6) — a server refusal can only ever be shown as ONE line for the
    // whole form. Checking first is what lets each message sit against its own field.
    //
    // 🔴 The server remains the enforcing side. These rules are a convenience and are allowed to be
    // wrong without the platform being wrong; every one of them has a backend test that refuses a bad
    // request with no browser involved.
    const errors: Record<string, string> = validateRegisterFields(values);
    const identityError = identityNumberError(identity);
    if (identityError) errors.identity_number = identityError;
    setIdentityTaken(false);
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});

    setLoading(true);
    setError('');
    try {
      await api.post('/auth/register', {
        ...values,
        identity_number: identityNumberBody(identity),
      });
      setDone(true);
    } catch (err) {
      // 366 (FR-007): the duplicate sits on the field, with a way to reach Carii; it names no account.
      const refusal = err as { code?: string; message?: string };
      if (refusal?.code === 'IDENTITY_NUMBER_TAKEN') {
        setFieldErrors({ identity_number: refusal.message ?? '' });
        setIdentityTaken(true);
        setLoading(false);
        return;
      }
      setError((err as { message?: string }).message ?? 'Registration failed');
      setLoading(false);
    }
  }

  /** Props that link an input to its error message, so the error is announced rather than merely shown. */
  function errorProps(field: string) {
    return fieldErrors[field]
      ? { 'aria-invalid': 'true' as const, 'aria-describedby': `${field}-error` }
      : {};
  }

  /** 348 (#952): a name field points at the IC sentence as well as at its own error, if it has one. */
  function nameFieldProps(field: string) {
    const own = errorProps(field);
    return {
      ...own,
      'aria-describedby': [own['aria-describedby'], NAME_IC_HINT_ID].filter(Boolean).join(' '),
    };
  }

  if (done) {
    return (
      <div className="page-fill bg-bg flex items-center justify-center px-4">
        <div className="text-center max-w-sm">
          <div className="mb-4 flex justify-center">
            <Mail size={40} className="text-primary" aria-hidden="true" />
          </div>
          <h2 className="text-xl font-bold text-text mb-2">Check your email</h2>
          <p className="text-gray-400">We sent a verification link to your email address.</p>
          <button
            type="button"
            onClick={() => route('/account/login')}
            className="mt-6 text-primary hover:underline text-sm"
          >
            Go to login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-fill bg-bg flex flex-col items-center justify-center px-4">
      <div className="w-full max-w-sm">
        <h1 className="text-2xl font-bold text-text mb-6">Create Account</h1>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1">
              <label htmlFor="first_name" className={FIELD_LABEL_CLASS}>
                First Name
              </label>
              <input
                id="first_name"
                name="first_name"
                data-testid="first-name"
                required
                {...nameFieldProps('first_name')}
                className="bg-surface-mid text-text rounded-lg px-4 py-3"
              />
              <FieldError field="first_name" message={fieldErrors.first_name} />
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor="last_name" className={FIELD_LABEL_CLASS}>
                Last Name
              </label>
              <input
                id="last_name"
                name="last_name"
                data-testid="last-name"
                required
                {...nameFieldProps('last_name')}
                className="bg-surface-mid text-text rounded-lg px-4 py-3"
              />
              <FieldError field="last_name" message={fieldErrors.last_name} />
            </div>
          </div>
          <p
            id={NAME_IC_HINT_ID}
            data-testid="name-ic-hint"
            className="-mt-2 text-xs text-gray-400"
          >
            {NAME_IC_HINT}
          </p>
          <div className="flex flex-col gap-1">
            <label htmlFor="email" className={FIELD_LABEL_CLASS}>
              Email
            </label>
            <input
              id="email"
              type="email"
              name="email"
              data-testid="email"
              required
              {...errorProps('email')}
              className="bg-surface-mid text-text rounded-lg px-4 py-3"
            />
            <FieldError field="email" message={fieldErrors.email} />
          </div>
          <div className="flex flex-col gap-1">
            <label htmlFor="phone" className={FIELD_LABEL_CLASS}>
              Phone Number
            </label>
            <input
              id="phone"
              type="tel"
              name="phone"
              data-testid="phone"
              required
              autoComplete="tel"
              {...errorProps('phone')}
              className="bg-surface-mid text-text rounded-lg px-4 py-3"
            />
            <FieldError field="phone" message={fieldErrors.phone} />
          </div>
          <IdentityNumberField
            value={identity}
            onChange={(v) => {
              setIdentity(v);
              setIdentityTaken(false);
            }}
            error={fieldErrors.identity_number}
            errorAction={
              identityTaken ? (
                <SupportLink className="text-secondary-bright underline">Contact us</SupportLink>
              ) : undefined
            }
          />
          <div className="flex flex-col gap-1">
            <label htmlFor="password" className={FIELD_LABEL_CLASS}>
              Password
            </label>
            <div className="relative">
              <input
                id="password"
                {...(showPassword ? { type: 'text' as const } : { type: 'password' as const })}
                name="password"
                data-testid="password"
                required
                minLength={8}
                className="bg-surface-mid text-text rounded-lg px-4 py-3 w-full pr-11"
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-200"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24" />
                    <line x1="1" y1="1" x2="23" y2="23" />
                  </svg>
                ) : (
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                )}
              </button>
            </div>
            {/* FR-015: the rule is stated before submitting, not discovered by being refused. */}
            <span id="password-rule" className="text-xs text-text-muted">
              {PASSWORD_RULE_TEXT}
            </span>
            <FieldError field="password" message={fieldErrors.password} />
          </div>
          {error && <p className="text-sm text-red-400">{error}</p>}
          <Button type="submit" loading={loading} size="lg" className="w-full">
            Create Account
          </Button>
        </form>
        <div className="mt-4 text-center">
          <a href="/account/login" className="text-sm text-gray-400 hover:underline">
            Already have an account? Sign in
          </a>
        </div>
      </div>
    </div>
  );
}
